import React, { useState } from 'react';
import styled from 'styled-components';
import { Breakpoints, Grid } from '../../../app/StyleGuide';
import { LinkStyleButton, PrimaryButton } from '../../../components/Buttons';
import ErrorMessage from '../../../components/ErrorMessage';
import trainingService from '../../../services/trainingService';
import windowService from '../../../services/windowService';
import { ConnectChurchAccountModal, useConnectChurchAccountModalControl } from '../../churchAccount';
import { ChurchItem } from '../../churchAccount/components/ChurchItem';
import {
  Container,
  Description,
  Subtitle,
  TitleContainer,
  TrainingName,
  TrainingPosterContainer,
} from './RequireOrgForTrainingViewElements';
import { TrainingPoster } from './TrainingHeader';

const ChurchItemWrapper = styled.div`
  width: 100%;
  max-width: 500px;
  margin-bottom: ${Grid._5};
`;

const AccountAction = styled(LinkStyleButton)`
  position: absolute;
  right: 0;
  bottom: 0;

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    right: ${Grid._3};
    bottom: ${Grid._3};
  }
`;

const ContinueButton = styled(PrimaryButton)`
  min-width: 200px;
`;

type VerifyOrgProps = {
  trainingId: string;
  trainingName: string;
  trainingPosterImage?: string;
  orgName: string;
  orgLogo?: string;
  orgChurchAccountId?: string;
  isOrgRestricted?: boolean;
  onChangeChurch: () => void;
};

export const VerifyOrg = ({
  trainingId,
  trainingName,
  trainingPosterImage,
  orgName,
  orgLogo,
  orgChurchAccountId,
  isOrgRestricted,
  onChangeChurch,
}: VerifyOrgProps) => {
  const churchAccountModal = useConnectChurchAccountModalControl();
  const [connected, setConnected] = useState(false);

  const onContinue = () => {
    if (window.featureToggles.get('AOM_ORG_CONNECTION') !== true) {
      return windowService.redirectTo(`/add-to-your-training/${trainingId}`);
    }

    if (orgChurchAccountId) {
      windowService.redirectTo(`/add-to-your-training/${trainingId}`);
    } else {
      churchAccountModal.open();
    }
  };

  return (
    <Container>
      {trainingPosterImage ? (
        <TrainingPosterContainer>
          <TrainingPoster src={trainingService.getTrainingImageUrl(trainingPosterImage)} alt={trainingName} />
        </TrainingPosterContainer>
      ) : (
        <TitleContainer>
          <Subtitle>Get ready to start</Subtitle>
          <TrainingName>{trainingName}</TrainingName>
        </TitleContainer>
      )}

      <Description>
        Welcome! Let&apos;s make sure your church is set up properly to share and assign {trainingName}. We will ask you
        to confirm or provide some church information about {orgName} before you start so that we can serve you better.
      </Description>

      <ChurchItemWrapper>
        <ChurchItem name={orgName} logo={orgLogo} meta="">
          <AccountAction onClick={onChangeChurch}>Not the right church?</AccountAction>
        </ChurchItem>
      </ChurchItemWrapper>

      {isOrgRestricted ? (
        <>
          <ErrorMessage>
            You are unable to begin the training with this church. Please select another church.
          </ErrorMessage>
          <ContinueButton onClick={onChangeChurch}>Select Another Church</ContinueButton>
        </>
      ) : (
        <ContinueButton onClick={onContinue}>Continue</ContinueButton>
      )}

      {churchAccountModal.isOpen && (
        <ConnectChurchAccountModal
          preSelectedAccountId={churchAccountModal.churchAccountId}
          onSuccess={() => setConnected(true)}
          dismissHandler={() => {
            churchAccountModal.close();
            if (connected) {
              windowService.redirectTo(`/add-to-your-training/${trainingId}`);
            }
          }}
          redirect={{ action: 'link', redirectPath: window.location.hash }}
          contentOverrides={{
            confirmation: {
              title: 'Before We Continue',
              subtitle:
                'We found an existing account profile for your church. Sync it with your Ministry Grid church to connect your information and improve your experience.',
            },
            create: {
              title: 'Before We Continue',
              subtitle:
                "Set your church up for the best experience by creating a Lifeway church account. You'll be redirected back here to continue to the next step.",
            },
          }}
        />
      )}
    </Container>
  );
};
